/**
 * Role Continuous Learning API — the "Training" section of a role (migration
 * 0112, spec CHAOS_ROLE_LEARNING_SPEC.md §C2/§D).
 *
 * Mirrors apps/chaos-gateway/src/role-learning.ts. Flag-gated server-side
 * (ROLE_LEARNING_ENABLED, default OFF) — every route 404s when the flag is OFF,
 * so the UI section + row badges must degrade gracefully on 404 (render nothing).
 *
 * An "addition" is one git-style role improvement: proposed by an agent
 * in-session, approved (→ gets a per-role commit number `seq`, shown as `L14`),
 * rejected, reverted, or superseded. Approved additions are injected into every
 * future spawn of the role (fenced by scope/org), so approval is the gate that
 * puts a standing instruction into the model's mind.
 */
import { apiJson } from './client';

export type RoleAdditionStatus =
  | 'proposed'
  | 'approved'
  | 'rejected'
  | 'reverted'
  | 'superseded';
export type RoleAdditionScope = 'org' | 'global';
export type RoleAdditionKind = 'instruction' | 'kb_doc';
export type ScopeConfidence = 'confident' | 'unsure';
export type ProposedBy = 'agent' | 'user' | 'distiller';

/** A poison-guard hit surfaced on a proposal. `blocking` families
 *  (pipe-to-shell / exfil / instruction-override / identity-subversion) require
 *  an explicit "I reviewed this" confirm before Approve; `info` families
 *  (standing-every / from-now-on) render as an informational chip only. */
export interface PoisonFlag {
  family: string;
  severity: 'info' | 'blocking';
  message?: string;
}

/** Full, server-resolved provenance (git-blame for a learning). Never
 *  agent-supplied — resolved from the JWT + session→card chain at propose time. */
export interface RoleAdditionProvenance {
  proposedBy: ProposedBy;
  proposedByUserName: string | null;
  proposedByRoleName: string | null;
  sessionId: string | null;
  sessionRunId: string | null;
  cardId: string | null;
  cardTitle: string | null;
  boardId: string | null;
  boardName: string | null;
  projectId: string | null;
  projectName: string | null;
  originOrgId: string | null;
  originOrgName: string | null;
}

export interface RoleAddition {
  id: string;
  roleId: string;
  scope: RoleAdditionScope;
  orgId: string | null;
  orgName: string | null;
  kind: RoleAdditionKind;
  title: string;
  content: string;
  agentSummary: string | null;
  scopeConfidence: ScopeConfidence;
  status: RoleAdditionStatus;
  /** per-role commit number, assigned at approval; null while proposed. */
  seq: number | null;
  supersedesId: string | null;
  supersedesSeq: number | null;
  revertOfId: string | null;
  userComment: string | null;
  rationale: string | null;
  poisonFlags: PoisonFlag[];
  editedBeforeApproval: boolean;
  originalContent: string | null;
  reviewedByName: string | null;
  reviewedAt: string | null;
  reviewNote: string | null;
  createdAt: string;
  approvedAt: string | null;
  revertedAt: string | null;
  revertedByName: string | null;
  provenance: RoleAdditionProvenance;
}

/** Prompt-injection budget footer (spec §C4/§D2). */
export interface RoleLearningBudget {
  usedBytes: number;
  maxBytes: number;
  count: number;
  maxCount: number;
}

export interface RoleImprovementsResponse {
  additions: RoleAddition[];
  pendingCount: number;
  budget: RoleLearningBudget;
  /** Server-resolved approval authority (spec §E6). global = account owner /
   *  system founder; org = account owner or that org's admin. An org admin can
   *  NEVER approve/edit a global addition, so the two are surfaced separately. */
  canApproveGlobal?: boolean;
  canApproveOrg?: boolean;
}

export type RoleImprovementStatusFilter = 'proposed' | 'approved' | 'all';

export interface DecideImprovementInput {
  action: 'approve' | 'reject';
  /** Owner-edited content — presence flips edited_before_approval + preserves original. */
  content?: string;
  title?: string;
  /** Reviewer's final scope (wins over the agent's guess). */
  scope?: RoleAdditionScope;
  orgId?: string | null;
  note?: string;
}

export const roleImprovementsApi = {
  /**
   * List a role's additions (proposals + history).
   *
   * NORMALISED AT THE DOOR, and this one is not about a matrix. `additions` is
   * declared required above and read as `q.data.additions.length` by
   * `useRolePendingCount` (role-training-section.tsx:766) — a hook mounted by
   * `<RolePendingBadge>` on EVERY ROW of the Roles & agents matrix and of
   * /company/roles. A 200 whose body has no `additions` therefore does not
   * blank a badge, it throws during render and the settings error boundary
   * takes the entire Roles pane, which is precisely how the 2026-08-01
   * credentials outage looked to the founder.
   *
   * `pendingCount` is the guard's own escape hatch and does not close this:
   * `q.data.pendingCount ?? q.data.additions.length` evaluates the right-hand
   * side whenever the count is absent, which is exactly the payload that would
   * also be missing the list.
   *
   * FOUND BY THE BROWSER, NOT BY GREP: the first run of
   * tests/browser-proof-settings/catalog-shape-proof.spec.ts put the Roles pane
   * into its error boundary with `TypeError: Cannot read properties of
   * undefined (reading 'length')` — the same sentence as the credentials
   * defect, one word different.
   *
   * `[]` is the right fallback rather than a throw: the route already 404s
   * whenever ROLE_LEARNING_ENABLED is off, and the hook's answer for that is
   * "no proposals". A badge that does not appear is a smaller lie than a
   * settings section that will not open.
   */
  list(
    roleId: string,
    status: RoleImprovementStatusFilter = 'all',
  ): Promise<RoleImprovementsResponse> {
    return apiJson<RoleImprovementsResponse>(
      `/roles/${roleId}/improvements?status=${status}`,
    ).then((r) => {
      // `poisonFlags` too: role-training-section.tsx:230 and :400 read
      // `a.poisonFlags.some(...)` during render. The gateway parses that jsonb
      // column through `parsePoisonFlags` and so always sends a list today —
      // which is the same thing that was true of `viaAllOrgIds` until it wasn't.
      const additions = (Array.isArray(r?.additions) ? r.additions : []).map((a) => ({
        ...a,
        poisonFlags: Array.isArray(a?.poisonFlags) ? a.poisonFlags : [],
      }));
      return {
        ...r,
        additions,
        pendingCount:
          typeof r?.pendingCount === 'number'
            ? r.pendingCount
            : additions.filter((a) => a.status === 'proposed').length,
      };
    });
  },

  /** Approve / reject a proposal. Approve assigns the next `seq` server-side. */
  decide(
    roleId: string,
    additionId: string,
    input: DecideImprovementInput,
  ): Promise<RoleAddition> {
    return apiJson<RoleAddition>(
      `/roles/${roleId}/improvements/${additionId}/decide`,
      { method: 'POST', json: input },
    );
  },

  /** Revert / un-revert an approved addition (drops it out of / back into every
   *  subsequent spawn; the history row is kept either way). */
  setReverted(
    roleId: string,
    additionId: string,
    reverted: boolean,
  ): Promise<RoleAddition> {
    return apiJson<RoleAddition>(
      `/roles/${roleId}/improvements/${additionId}/revert`,
      { method: 'POST', json: { reverted } },
    );
  },

  /** Consolidate (squash) several approved additions into one merged addition
   *  that supersedes them (spec §D2 budget footer). */
  consolidate(
    roleId: string,
    input: { supersedeIds: string[]; title: string; content: string; scope: RoleAdditionScope; orgId?: string | null },
  ): Promise<RoleAddition> {
    return apiJson<RoleAddition>(`/roles/${roleId}/improvements/consolidate`, {
      method: 'POST',
      json: input,
    });
  },
};

export const roleImprovementsKeys = {
  all: ['role-improvements'] as const,
  list: (roleId: string, status: RoleImprovementStatusFilter) =>
    [...roleImprovementsKeys.all, roleId, status] as const,
  pending: (roleId: string) =>
    [...roleImprovementsKeys.all, roleId, 'proposed'] as const,
};
